import React, { useEffect, Suspense, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Container from '@mui/material/Container';

import { NotFound } from 'scenes';
import { Footer, ScrollTop, Breadcrumbs } from 'ui';
import { SearchBar } from 'modules/common';
import { Navbar, Subnavigation } from 'modules/common/Navbar';

import { generateKey, redirectFromHashLinks } from 'shared/functions';
import { setIndustriesMapping } from 'shared/mappings/industries/industriesMapping';
import { getPreferencesAction } from 'store/newsFeed/newsFeedActions';
import { getLoggedUserAction } from 'store/login/loginAction';
import {
  fetchShopPermissionsAction,
  fetchShopProductsAction,
} from 'modules/shop/store/shopActions';
import { InitialSubscriptionInvitiationModal } from 'modules/creditReview/CreditReviewModals';
import { CreditSphereInvitiationModal } from 'modules/common/Modals/CreditSphereInvitationModal';
import { getIndustriesAction } from 'store/shared/industries/industriesActions';
import { requestRelatedEntitiesAction } from 'store/shared/relatedEntities/relatedEntitiesActions';
import { requestCreditRequestLevelsAction } from 'store/shared/creditReviewLevels/creditReviewLevelsActions';
import { requestQuestionnaireAvailabilityAction } from 'store/shared/questionnaire/questionnaireActions';
import { getESGCoverageAction } from 'store/shared/esgCoverage/esgCoverageAction';
import { getRegionsAction } from 'store/shared/regions/regionsAction';
import { getRatingCoverageAction } from 'store/shared/ratingCoverage/ratingCoverageAction';
import { getCreditReviewContractDataAction } from 'store/creditReview/creditReviewActions';
import { PrivateRoute } from 'shared/hoc/PrivateRoute/PrivateRoute';
import { useReportPage, useESGReportPage, useAdminPage } from 'shared/hooks';
import { creditReviewInitialSubscription } from 'shared/constants/creditReview/creditReviewInitialSubscription';
import { creditSphereInitialSubscription } from 'shared/constants/creditSphere/creditSphereInitialSubscription';
import { saveOnboardingState, loadOnboardingState } from 'store/localStorage';
import { routes } from 'routes';
import { Loader } from 'ui/Loader/Loader';
import { requestConfirmationChangedAction } from 'store/entity/entityActions';
import { useFeatures } from 'shared/hooks/useFeatures/useFeatures';
import { ErrorBoundary } from 'shared/errors';
import { getShopProducts } from 'modules/shop/selectors/shopSelectors';
import { subscriptionStatuses } from 'modules/shop/shared/constants/shopSubscriptionStatuses';
import { requestCreditReviewSpecsAction } from 'store/shared/creditReviewSpecs/creditReviewSpecsActions';
import { getEsgContractDataAction } from 'store/esg/esgActions';

const Questionnaire = React.lazy(() => import('modules/common/Questionnaire/Questionnaire'));

const qualificationId = 1;

const AuthenticatedApp = () => {
  const dispatch = useDispatch();
  const industries = useSelector(({ shared }) => shared.industries.data);
  const authData = useSelector(({ auth: { data } }) => data);
  // const showQuestionnaire = useSelector(getSharedQuestionareShow);
  const showQuestionnaire = false;
  const { autoConfirm } = useSelector(({ entity }) => entity.requestConfirmation);
  const features = useFeatures();

  const { data: subscriptionsData, isPending: subscriptionsIsPending } =
    useSelector(getShopProducts);

  const creditReviewSubscription = creditReviewInitialSubscription(subscriptionsData);
  const creditSphereSubscription = creditSphereInitialSubscription(subscriptionsData);

  const showCreditReviewInvitationModal =
    features.creditReviewInvitationCard &&
    !subscriptionsIsPending &&
    creditReviewSubscription &&
    creditReviewSubscription.status === subscriptionStatuses.NO_SUBSCRIPTION &&
    !loadOnboardingState();

  const showCreditSphereInvitationModal =
    features.creditSphereInvitationCard &&
    !subscriptionsIsPending &&
    creditSphereSubscription &&
    creditSphereSubscription.status === subscriptionStatuses.NO_SUBSCRIPTION &&
    !loadOnboardingState();

  const [initialSubscriptionInvitationModalOpen, setInitialSubscriptionInvitationModalOpen] =
    useState(false);

  const [creditSphereInvitationModalOpen, setCreditSphereInvitationModalOpen] = useState(false);

  useEffect(() => {
    if (!subscriptionsIsPending) {
      setInitialSubscriptionInvitationModalOpen(showCreditReviewInvitationModal);
      setCreditSphereInvitationModalOpen(showCreditSphereInvitationModal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionsIsPending]);

  const toggleInitialSubscriptionInvitationModal = () =>
    setInitialSubscriptionInvitationModalOpen(!initialSubscriptionInvitationModalOpen);

  const toggleCreditSphereInvitationModal = () =>
    setCreditSphereInvitationModalOpen(!creditSphereInvitationModalOpen);

  const navigateToCreditReviewSubscription = () => {
    saveOnboardingState(true);
    window.location.href = `/ca/subscriptions?productId=${
      creditReviewSubscription ? creditReviewSubscription.id : undefined
    }&freeTrial=true`;
  };

  const navigateToCreditSphereSubscription = () => {
    saveOnboardingState(true);
    window.location.href = `/ca/subscriptions?productId=${
      creditSphereSubscription ? creditSphereSubscription.id : undefined
    }&freeTrial=true`;
  };

  const getRelatedEntitiesData = () => {
    const { relatedIssuers } = authData.organisation;
    if (relatedIssuers && relatedIssuers.length) {
      dispatch(requestRelatedEntitiesAction({ ids: relatedIssuers.map(({ id }) => id) }));
    }
  };

  const getRatingCoverage = () => {
    dispatch(getRatingCoverageAction);
  };

  useEffect(() => {
    if (!useESGReportPage) {
      if (process.env.REACT_APP_ENV === 'production') {
        FS.identify(authData.userId, {
          displayName: `${authData.firstName} ${authData.name}`,
          email: authData.user?.email,
        });
      }
      if (features.questionnaire) {
        dispatch(
          requestQuestionnaireAvailabilityAction({ qualificationId, userId: authData.userId }),
        );
      }
      if (features.creditReview) {
        dispatch(getCreditReviewContractDataAction);
      }
      if (features.esgCompanies) {
        dispatch(getEsgContractDataAction);
      }
      dispatch(getPreferencesAction());
      dispatch(getIndustriesAction);
      dispatch(requestCreditRequestLevelsAction());
      dispatch(requestCreditReviewSpecsAction());
      dispatch(getLoggedUserAction);
      dispatch(fetchShopPermissionsAction());
      dispatch(getESGCoverageAction);
      dispatch(getRegionsAction);
      getRatingCoverage();
      getRelatedEntitiesData();
      dispatch(fetchShopProductsAction());

      if (localStorage.getItem('requestAutoConfirm')) {
        dispatch(requestConfirmationChangedAction({ confirmed: true, autoConfirm: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoConfirm && !localStorage.getItem('requestAutoConfirm')) {
      localStorage.setItem('requestAutoConfirm', true);
    }
  }, [autoConfirm]);

  useEffect(() => {
    setIndustriesMapping(industries);
  }, [industries]);
  const renderRoutes = (
    <Routes>
      {redirectFromHashLinks()}
      {routes.map(
        (
          {
            path,
            component,
            exact,
            environment,
            role,
            permission,
            licence,
            shopPermissions,
            ...rest
          },
          index,
        ) =>
          component && (
            <Route
              key={generateKey(index, path.replace('/', '-'))}
              exact={exact}
              path={path}
              element={
                <PrivateRoute
                  path={path}
                  component={component}
                  exact={exact}
                  environment={environment}
                  role={role}
                  permission={permission}
                  licence={licence}
                  shopPermissions={shopPermissions}
                  {...rest}
                />
              }
            />
          ),
      )}
      <Route component={NotFound} />
    </Routes>
  );

  if (useAdminPage) {
    return (
      <>
        <BrowserRouter basename={process.env.PUBLIC_URL}>
          <Navbar id="back-to-top-anchor" />
          <Subnavigation />
          <ErrorBoundary showErrorPage>
            <Container>
              <Breadcrumbs separator=">" />
              {renderRoutes}
            </Container>
          </ErrorBoundary>
        </BrowserRouter>
        <Footer />
        <ScrollTop />
        {features.creditReviewInvitationCard && (
          <InitialSubscriptionInvitiationModal
            open={initialSubscriptionInvitationModalOpen}
            onClose={toggleInitialSubscriptionInvitationModal}
            onNavigateToInitialSubscription={navigateToCreditReviewSubscription}
          />
        )}
        {features.creditSphereInvitationCard && (
          <CreditSphereInvitiationModal
            open={creditSphereInvitationModalOpen}
            onClose={toggleCreditSphereInvitationModal}
            onNavigateToInitialSubscription={navigateToCreditSphereSubscription}
          />
        )}
      </>
    );
  }

  return useReportPage ? (
    <BrowserRouter basename={process.env.PUBLIC_URL}>{renderRoutes}</BrowserRouter>
  ) : (
    <>
      <BrowserRouter basename={process.env.PUBLIC_URL}>
        <Suspense fallback={<Loader center />}>
          {showQuestionnaire && <Questionnaire qualificationId={qualificationId} />}
        </Suspense>
        <Navbar id="back-to-top-anchor" />
        <Subnavigation />
        <ErrorBoundary showErrorPage>
          <Container>
            <SearchBar />
            <Breadcrumbs />
            {renderRoutes}
          </Container>
        </ErrorBoundary>
      </BrowserRouter>
      <Footer />
      <ScrollTop />
      {features.creditReviewInvitationCard && (
        <InitialSubscriptionInvitiationModal
          open={initialSubscriptionInvitationModalOpen}
          onClose={toggleInitialSubscriptionInvitationModal}
          onNavigateToInitialSubscription={navigateToCreditReviewSubscription}
        />
      )}
      {features.creditSphereInvitationCard && (
        <CreditSphereInvitiationModal
          open={creditSphereInvitationModalOpen}
          onClose={toggleCreditSphereInvitationModal}
          onNavigateToInitialSubscription={navigateToCreditSphereSubscription}
        />
      )}
    </>
  );
};

export default AuthenticatedApp;
